import React, { useState, useEffect, useCallback, useRef } from 'react';

// ─── Storms in Paris Palette ───
const colors = {
    bg: '#1b363b',
    card: '#22434a',
    cardHover: '#294f56',
    accent: '#3a7a7a',
    accentLight: '#4a9090',
    gold: '#d4a764',
    goldLight: '#e0be88',
    text: '#c8d0cb',
    textMuted: '#7a8a80',
    textLight: '#e8ede9',
    border: '#2d4d52',
    danger: '#c44040',
    success: '#4a9a6a',
};

const fonts = {
  heading: "'Playfair Display', serif",
  body: "'DM Sans', sans-serif",
};

// ─── Crypto Utils ───
const cryptoUtils = {
  async deriveKey(password, salt) {
    const enc = new TextEncoder();
    const keyMaterial = await crypto.subtle.importKey('raw', enc.encode(password), 'PBKDF2', false, ['deriveKey']);
    return crypto.subtle.deriveKey(
      { name: 'PBKDF2', salt, iterations: 100000, hash: 'SHA-256' },
      keyMaterial,
      { name: 'AES-GCM', length: 256 },
      false,
      ['encrypt', 'decrypt']
    );
  },
  async encrypt(text, password) {
    const enc = new TextEncoder();
    const salt = crypto.getRandomValues(new Uint8Array(16));
    const iv = crypto.getRandomValues(new Uint8Array(12));
    const key = await this.deriveKey(password, salt);
    const encrypted = await crypto.subtle.encrypt({ name: 'AES-GCM', iv }, key, enc.encode(text));
    const combined = new Uint8Array(salt.length + iv.length + new Uint8Array(encrypted).length);
    combined.set(salt, 0);
    combined.set(iv, salt.length);
    combined.set(new Uint8Array(encrypted), salt.length + iv.length);
    return btoa(String.fromCharCode(...combined));
  },
  async decrypt(data, password) {
    const raw = Uint8Array.from(atob(data), c => c.charCodeAt(0));
    const salt = raw.slice(0, 16);
    const iv = raw.slice(16, 28);
    const encrypted = raw.slice(28);
    const key = await this.deriveKey(password, salt);
    const decrypted = await crypto.subtle.decrypt({ name: 'AES-GCM', iv }, key, encrypted);
    return new TextDecoder().decode(decrypted);
  },
  async hash(text) {
    const enc = new TextEncoder();
    const hashBuffer = await crypto.subtle.digest('SHA-256', enc.encode(text));
    return btoa(String.fromCharCode(...new Uint8Array(hashBuffer)));
  }
};

// ─── Navigation Component ───
function Nav({ page, setPage }) {
  const [menuOpen, setMenuOpen] = useState(false);
  const links = [
    { id: 'home', label: 'Home' },
    { id: 'about', label: 'About' },
    { id: 'services', label: 'Services' },
    { id: 'vault', label: '🔒' },
  ];
  return (
    <nav style={{
      position: 'fixed', top: 0, left: 0, right: 0, zIndex: 1000,
      background: colors.bg + 'ee', backdropFilter: 'blur(20px)',
      borderBottom: `1px solid ${colors.border}`,
      fontFamily: fonts.body,
    }}>
      <div style={{
        maxWidth: 1200, margin: '0 auto', padding: '0 24px',
        display: 'flex', alignItems: 'center', justifyContent: 'space-between', height: 64,
      }}>
        <div style={{ cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 12 }}
          onClick={() => setPage('home')}>
          <span style={{ fontFamily: fonts.heading, fontSize: 20, color: colors.gold, fontWeight: 700 }}>
            Bad Strategy Group
          </span>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          {links.map(l => (
            <button key={l.id} onClick={() => { setPage(l.id); setMenuOpen(false); }}
              style={{
                background: page === l.id ? colors.accent : 'transparent',
                color: page === l.id ? colors.textLight : colors.textMuted,
                border: 'none', padding: '8px 16px', borderRadius: 8,
                cursor: 'pointer', fontFamily: fonts.body, fontSize: 14,
                fontWeight: page === l.id ? 600 : 400,
                transition: 'all 0.2s',
              }}>
              {l.label}
            </button>
          ))}
        </div>
      </div>
    </nav>
  );
}

// ─── Home Page ───
function HomePage({ setPage }) {
  const stats = [
    { num: '15+', label: 'Years Experience' },
    { num: '500+', label: 'Spellbook Prompts' },
    { num: '12', label: 'AI Agents Built' },
    { num: '17', label: 'Training Modules' },
  ];
  const capabilities = [
    { title: 'EHR Advisory', desc: 'Epic/Oracle Health optimization, workflow design, and clinical system strategy for health systems.' },
    { title: 'AI Enablement', desc: 'Custom AI agent development, prompt engineering frameworks, and enterprise automation architecture.' },
    { title: 'RCM Transformation', desc: 'Revenue cycle optimization through intelligent automation, denial management, and process redesign.' },
    { title: 'Training & Development', desc: 'Comprehensive AI literacy programs, from fundamentals to advanced multi-agent system design.' },
    { title: 'Strategic Consulting', desc: 'Technology strategy, vendor evaluation, implementation planning, and digital transformation roadmaps.' },
    { title: 'Process Architecture', desc: 'End-to-end workflow analysis, automation opportunity identification, and operational excellence frameworks.' },
  ];
  return (
    <div style={{ fontFamily: fonts.body }}>
      {/* Hero */}
      <section style={{
        minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center',
        padding: '120px 24px 80px', position: 'relative', overflow: 'hidden',
      }}>
        <div style={{
          position: 'absolute', top: '20%', right: '10%', width: 400, height: 400,
          background: `radial-gradient(circle, ${colors.accent}15 0%, transparent 70%)`,
          borderRadius: '50%', filter: 'blur(60px)',
        }} />
        <div style={{ maxWidth: 1200, width: '100%', display: 'flex', flexWrap: 'wrap', gap: 48, alignItems: 'center', position: 'relative', zIndex: 1 }}>
          <div style={{ flex: '1 1 500px' }}>
            <p style={{ color: colors.gold, fontSize: 14, fontWeight: 600, letterSpacing: 2, textTransform: 'uppercase', marginBottom: 16 }}>
              Healthcare Technology & AI Strategy
            </p>
            <h1 style={{ fontFamily: fonts.heading, fontSize: 56, color: colors.textLight, lineHeight: 1.1, marginBottom: 24 }}>
              Where Clinical Expertise<br />
              <span style={{ color: colors.gold }}>Meets Artificial Intelligence</span>
            </h1>
            <p style={{ color: colors.textMuted, fontSize: 18, lineHeight: 1.7, maxWidth: 560, marginBottom: 40 }}>
              Bridging the gap between healthcare operations and emerging AI capabilities.
              From EHR optimization to multi-agent automation systems.
            </p>
            <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
              <button onClick={() => setPage('services')} style={{
                background: colors.gold, color: colors.bg, border: 'none', padding: '14px 32px',
                borderRadius: 8, fontSize: 16, fontWeight: 600, cursor: 'pointer',
                fontFamily: fonts.body, transition: 'all 0.2s',
              }}>
                View Services
              </button>
              <button onClick={() => setPage('about')} style={{
                background: 'transparent', color: colors.gold,
                border: `1px solid ${colors.gold}40`, padding: '14px 32px',
                borderRadius: 8, fontSize: 16, fontWeight: 600, cursor: 'pointer',
                fontFamily: fonts.body, transition: 'all 0.2s',
              }}>
                About Me
              </button>
            </div>
          </div>
          <div style={{
            flex: '0 0 320px', background: colors.card, borderRadius: 16,
            border: `1px solid ${colors.border}`, padding: 32,
          }}>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 24 }}>
              {stats.map((s, i) => (
                <div key={i} style={{ textAlign: 'center' }}>
                  <div style={{ fontFamily: fonts.heading, fontSize: 32, color: colors.gold, fontWeight: 700 }}>{s.num}</div>
                  <div style={{ color: colors.textMuted, fontSize: 12, marginTop: 4, lineHeight: 1.3 }}>{s.label}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Capabilities */}
      <section style={{ padding: '80px 24px', maxWidth: 1200, margin: '0 auto' }}>
        <h2 style={{ fontFamily: fonts.heading, fontSize: 36, color: colors.textLight, textAlign: 'center', marginBottom: 48 }}>
          Core <span style={{ color: colors.gold }}>Capabilities</span>
        </h2>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', gap: 24 }}>
          {capabilities.map((c, i) => (
            <div key={i} style={{
              background: colors.card, borderRadius: 12, padding: 28,
              border: `1px solid ${colors.border}`, transition: 'all 0.2s',
            }}>
              <h3 style={{ fontFamily: fonts.heading, fontSize: 20, color: colors.textLight, marginBottom: 12 }}>{c.title}</h3>
              <p style={{ color: colors.textMuted, fontSize: 14, lineHeight: 1.7 }}>{c.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section style={{
        padding: '80px 24px', textAlign: 'center',
        background: `linear-gradient(135deg, ${colors.accent}15, ${colors.gold}10)`,
        borderTop: `1px solid ${colors.border}`, borderBottom: `1px solid ${colors.border}`,
      }}>
        <h2 style={{ fontFamily: fonts.heading, fontSize: 36, color: colors.textLight, marginBottom: 16 }}>
          Ready to <span style={{ color: colors.gold }}>Transform</span>?
        </h2>
        <p style={{ color: colors.textMuted, fontSize: 18, maxWidth: 600, margin: '0 auto 32px' }}>
          Whether you need EHR optimization, AI strategy, or revenue cycle transformation —
          let's build something that works.
        </p>
        <a href="mailto:brady@badstrategygroup.com" style={{
          display: 'inline-block', background: colors.gold, color: colors.bg,
          padding: '14px 40px', borderRadius: 8, fontSize: 16, fontWeight: 600,
          textDecoration: 'none', fontFamily: fonts.body,
        }}>
          Get In Touch
        </a>
      </section>
    </div>
  );
}

// ─── About Page ───
function AboutPage() {
  const skills = [
    'Epic EHR (Certified)', 'Oracle Health / Cerner', 'AI Agent Architecture',
    'Prompt Engineering', 'Revenue Cycle Management', 'Clinical Workflows',
    'Multi-Agent Systems', 'Process Automation', 'Training & Development',
    'Strategic Planning', 'Change Management', 'Data Analytics',
  ];
  return (
    <div style={{ fontFamily: fonts.body, padding: '120px 24px 80px', maxWidth: 1200, margin: '0 auto' }}>
      <div style={{ display: 'flex', gap: 48, flexWrap: 'wrap' }}>
        <div style={{ flex: '1 1 500px' }}>
          <p style={{ color: colors.gold, fontSize: 14, fontWeight: 600, letterSpacing: 2, textTransform: 'uppercase', marginBottom: 16 }}>
            About
          </p>
          <h1 style={{ fontFamily: fonts.heading, fontSize: 44, color: colors.textLight, lineHeight: 1.2, marginBottom: 24 }}>
            Brady <span style={{ color: colors.gold }}>Defibaugh</span>
          </h1>
          <div style={{ color: colors.text, fontSize: 16, lineHeight: 1.8 }}>
            <p style={{ marginBottom: 16 }}>
              Healthcare technology strategist with 15+ years of experience spanning EHR implementation,
              revenue cycle optimization, and AI-driven transformation. I work at the intersection of
              clinical operations and emerging technology — translating complex AI capabilities into
              practical healthcare solutions.
            </p>
            <p style={{ marginBottom: 16 }}>
              My background combines deep EHR expertise (Epic, Oracle Health) with hands-on AI development,
              including a 500+ prompt Spellbook library, 12 custom AI agents, and a 17-module enterprise
              training program. I believe the future of healthcare operations is intelligent automation —
              and I build the bridges to get there.
            </p>
            <p>
              As the founder of Bad Strategy Group, I help healthcare organizations cut through the noise
              and implement AI strategies that actually work — not just demos and proofs of concept, but
              production-grade systems that improve operations and outcomes.
            </p>
          </div>
        </div>
        <div style={{ flex: '0 0 320px' }}>
          <div style={{
            background: colors.card, borderRadius: 16,
            border: `1px solid ${colors.border}`, padding: 28,
          }}>
            <h3 style={{ fontFamily: fonts.heading, fontSize: 18, color: colors.gold, marginBottom: 20 }}>
              Skills & Expertise
            </h3>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
              {skills.map((s, i) => (
                <span key={i} style={{
                  background: colors.accent + '25', color: colors.accentLight,
                  padding: '6px 14px', borderRadius: 20, fontSize: 13, fontWeight: 500,
                  border: `1px solid ${colors.accent}40`,
                }}>
                  {s}
                </span>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Services Page ───
function ServicesPage({ setPage }) {
  const services = [
    {
      title: 'EHR Advisory & Optimization',
      desc: 'End-to-end EHR strategy, implementation support, and workflow optimization for Epic, Oracle Health, and other major platforms.',
      features: ['System selection & evaluation', 'Workflow design & optimization', 'Clinical decision support', 'Integration architecture', 'Training & adoption strategy'],
      featured: false,
    },
    {
      title: 'AI Enablement & Automation',
      desc: 'Transform your operations with custom AI solutions — from intelligent agents to enterprise-wide automation frameworks.',
      features: ['Custom AI agent development', 'Prompt engineering frameworks', 'Multi-agent orchestration', 'Enterprise AI training (17 modules)', 'Automation opportunity assessment'],
      featured: true,
    },
    {
      title: 'RCM Transformation',
      desc: 'Revenue cycle optimization through intelligent automation, predictive analytics, and process redesign.',
      features: ['Denial management automation', 'Prior authorization optimization', 'Coding accuracy improvement', 'A/R workflow automation', 'Financial analytics & dashboards'],
      featured: false,
    },
  ];
  return (
    <div style={{ fontFamily: fonts.body, padding: '120px 24px 80px', maxWidth: 1200, margin: '0 auto' }}>
      <div style={{ textAlign: 'center', marginBottom: 56 }}>
        <p style={{ color: colors.gold, fontSize: 14, fontWeight: 600, letterSpacing: 2, textTransform: 'uppercase', marginBottom: 16 }}>
          What We Do
        </p>
        <h1 style={{ fontFamily: fonts.heading, fontSize: 44, color: colors.textLight, marginBottom: 16 }}>
          Services & <span style={{ color: colors.gold }}>Solutions</span>
        </h1>
        <p style={{ color: colors.textMuted, fontSize: 18, maxWidth: 600, margin: '0 auto' }}>
          Comprehensive healthcare technology consulting — from EHR optimization to AI-powered transformation.
        </p>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(340px, 1fr))', gap: 24 }}>
        {services.map((s, i) => (
          <div key={i} style={{
            background: s.featured ? `linear-gradient(135deg, ${colors.accent}20, ${colors.gold}10)` : colors.card,
            borderRadius: 16, padding: 32,
            border: `1px solid ${s.featured ? colors.gold + '40' : colors.border}`,
            position: 'relative',
          }}>
            {s.featured && (
              <span style={{
                position: 'absolute', top: 16, right: 16,
                background: colors.gold, color: colors.bg,
                padding: '4px 12px', borderRadius: 12, fontSize: 11, fontWeight: 700,
                textTransform: 'uppercase', letterSpacing: 1,
              }}>Featured</span>
            )}
            <h3 style={{ fontFamily: fonts.heading, fontSize: 22, color: colors.textLight, marginBottom: 12 }}>{s.title}</h3>
            <p style={{ color: colors.textMuted, fontSize: 14, lineHeight: 1.7, marginBottom: 20 }}>{s.desc}</p>
            <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
              {s.features.map((f, j) => (
                <li key={j} style={{
                  color: colors.text, fontSize: 14, padding: '6px 0',
                  borderBottom: j < s.features.length - 1 ? `1px solid ${colors.border}` : 'none',
                  display: 'flex', alignItems: 'center', gap: 8,
                }}>
                  <span style={{ color: colors.gold, fontSize: 12 }}>✓</span> {f}
                </li>
              ))}
            </ul>
          </div>
        ))}
      </div>
      <div style={{ textAlign: 'center', marginTop: 56 }}>
        <a href="mailto:brady@badstrategygroup.com" style={{
          display: 'inline-block', background: colors.gold, color: colors.bg,
          padding: '14px 40px', borderRadius: 8, fontSize: 16, fontWeight: 600,
          textDecoration: 'none', fontFamily: fonts.body,
        }}>
          Start a Conversation
        </a>
      </div>
    </div>
  );
}

// ─── Vault Page ───
function VaultPage() {
  const [vaultState, setVaultState] = useState('locked'); // locked, setup, unlocked
  const [password, setPassword] = useState('');
  const [confirmPw, setConfirmPw] = useState('');
  const [items, setItems] = useState([]);
  const [activeCategory, setActiveCategory] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [showAdd, setShowAdd] = useState(false);
  const [newItem, setNewItem] = useState({ title: '', content: '', category: 'prompts', tags: '' });
  const [error, setError] = useState('');
  const [masterKey, setMasterKey] = useState('');
  const lastActivity = useRef(Date.now());

  const categories = [
    { id: 'all', label: 'All Assets' },
    { id: 'prompts', label: 'Prompts' },
    { id: 'skills', label: 'Skills' },
    { id: 'frameworks', label: 'Frameworks' },
    { id: 'documents', label: 'Documents' },
    { id: 'ideas', label: 'Ideas' },
    { id: 'credentials', label: 'Credentials' },
  ];

  useEffect(() => {
    const stored = localStorage.getItem('bsg_vault_hash');
    if (!stored) {
      setVaultState('setup');
    }
  }, []);

  // Auto-lock after 10 minutes
  useEffect(() => {
    if (vaultState !== 'unlocked') return;
    const interval = setInterval(() => {
      if (Date.now() - lastActivity.current > 600000) {
        handleLock();
      }
    }, 30000);
    const resetTimer = () => { lastActivity.current = Date.now(); };
    window.addEventListener('mousemove', resetTimer);
    window.addEventListener('keypress', resetTimer);
    return () => {
      clearInterval(interval);
      window.removeEventListener('mousemove', resetTimer);
      window.removeEventListener('keypress', resetTimer);
    };
  }, [vaultState]);

  const handleSetup = async () => {
    if (password.length < 8) { setError('Password must be 8+ characters'); return; }
    if (password !== confirmPw) { setError('Passwords do not match'); return; }
    const hash = await cryptoUtils.hash(password);
    localStorage.setItem('bsg_vault_hash', hash);
    setMasterKey(password);
    setVaultState('unlocked');
    setPassword('');
    setConfirmPw('');
    setError('');
  };

  const handleLogin = async () => {
    const storedHash = localStorage.getItem('bsg_vault_hash');
    const inputHash = await cryptoUtils.hash(password);
    if (inputHash === storedHash) {
      setMasterKey(password);
      // Decrypt items
      const encData = localStorage.getItem('bsg_vault_data');
      if (encData) {
        try {
          const decrypted = await cryptoUtils.decrypt(encData, password);
          setItems(JSON.parse(decrypted));
        } catch { setItems([]); }
      }
      setVaultState('unlocked');
      setPassword('');
      setError('');
    } else {
      setError('Invalid password');
    }
  };

  const handleLock = () => {
    setVaultState('locked');
    setMasterKey('');
    setItems([]);
    setPassword('');
    setShowAdd(false);
  };

  const saveItems = async (newItems) => {
    setItems(newItems);
    const encrypted = await cryptoUtils.encrypt(JSON.stringify(newItems), masterKey);
    localStorage.setItem('bsg_vault_data', encrypted);
  };

  const addItem = async () => {
    if (!newItem.title || !newItem.content) { setError('Title and content required'); return; }
    const item = {
      id: Date.now().toString(),
      ...newItem,
      tags: newItem.tags.split(',').map(t => t.trim()).filter(Boolean),
      created: new Date().toISOString(),
    };
    await saveItems([...items, item]);
    setNewItem({ title: '', content: '', category: 'prompts', tags: '' });
    setShowAdd(false);
    setError('');
  };

  const deleteItem = async (id) => {
    await saveItems(items.filter(i => i.id !== id));
  };

  const copyItem = (content) => {
    navigator.clipboard.writeText(content);
  };

  const filtered = items.filter(i => {
    const catMatch = activeCategory === 'all' || i.category === activeCategory;
    const searchMatch = !searchQuery || i.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      i.tags?.some(t => t.toLowerCase().includes(searchQuery.toLowerCase()));
    return catMatch && searchMatch;
  });

  const inputStyle = {
    width: '100%', padding: '12px 16px', background: colors.card,
    border: `1px solid ${colors.border}`, borderRadius: 8, color: colors.textLight,
    fontFamily: fonts.body, fontSize: 14, outline: 'none', boxSizing: 'border-box',
  };

  // ─── Setup Screen ───
  if (vaultState === 'setup') {
    return (
      <div style={{ fontFamily: fonts.body, padding: '120px 24px 80px', maxWidth: 480, margin: '0 auto', textAlign: 'center' }}>
        <div style={{ fontSize: 48, marginBottom: 16 }}>🔒</div>
        <h2 style={{ fontFamily: fonts.heading, fontSize: 28, color: colors.textLight, marginBottom: 8 }}>
          Initialize <span style={{ color: colors.gold }}>Vault</span>
        </h2>
        <p style={{ color: colors.textMuted, fontSize: 14, marginBottom: 32 }}>
          Set your master password. This encrypts everything with AES-256-GCM. No recovery — remember it.
        </p>
        {error && <p style={{ color: colors.danger, fontSize: 14, marginBottom: 16 }}>{error}</p>}
        <input type="password" placeholder="Master password (8+ chars)" value={password}
          onChange={e => setPassword(e.target.value)} style={{ ...inputStyle, marginBottom: 12 }} />
        <input type="password" placeholder="Confirm password" value={confirmPw}
          onChange={e => setConfirmPw(e.target.value)}
          onKeyDown={e => e.key === 'Enter' && handleSetup()}
          style={{ ...inputStyle, marginBottom: 20 }} />
        <button onClick={handleSetup} style={{
          width: '100%', padding: '14px', background: colors.gold, color: colors.bg,
          border: 'none', borderRadius: 8, fontSize: 16, fontWeight: 600,
          cursor: 'pointer', fontFamily: fonts.body,
        }}>
          Create Vault
        </button>
      </div>
    );
  }

  // ─── Login Screen ───
  if (vaultState === 'locked') {
    return (
      <div style={{ fontFamily: fonts.body, padding: '120px 24px 80px', maxWidth: 480, margin: '0 auto', textAlign: 'center' }}>
        <div style={{ fontSize: 48, marginBottom: 16 }}>🔐</div>
        <h2 style={{ fontFamily: fonts.heading, fontSize: 28, color: colors.textLight, marginBottom: 8 }}>
          Vault <span style={{ color: colors.gold }}>Login</span>
        </h2>
        <p style={{ color: colors.textMuted, fontSize: 14, marginBottom: 32 }}>
          Enter your master password to decrypt your assets.
        </p>
        {error && <p style={{ color: colors.danger, fontSize: 14, marginBottom: 16 }}>{error}</p>}
        <input type="password" placeholder="Master password" value={password}
          onChange={e => setPassword(e.target.value)}
          onKeyDown={e => e.key === 'Enter' && handleLogin()}
          style={{ ...inputStyle, marginBottom: 20 }} />
        <button onClick={handleLogin} style={{
          width: '100%', padding: '14px', background: colors.gold, color: colors.bg,
          border: 'none', borderRadius: 8, fontSize: 16, fontWeight: 600,
          cursor: 'pointer', fontFamily: fonts.body,
        }}>
          Unlock
        </button>
      </div>
    );
  }

  // ─── Unlocked Vault ───
  return (
    <div style={{ fontFamily: fonts.body, padding: '120px 24px 80px', maxWidth: 1200, margin: '0 auto' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 32, flexWrap: 'wrap', gap: 16 }}>
        <div>
          <h2 style={{ fontFamily: fonts.heading, fontSize: 28, color: colors.textLight, marginBottom: 4 }}>
            Your <span style={{ color: colors.gold }}>Vault</span>
          </h2>
          <p style={{ color: colors.textMuted, fontSize: 14 }}>{items.length} encrypted assets</p>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button onClick={() => setShowAdd(!showAdd)} style={{
            background: colors.gold, color: colors.bg, border: 'none', padding: '10px 20px',
            borderRadius: 8, fontSize: 14, fontWeight: 600, cursor: 'pointer', fontFamily: fonts.body,
          }}>
            {showAdd ? 'Cancel' : '+ Add Asset'}
          </button>
          <button onClick={handleLock} style={{
            background: colors.danger + '20', color: colors.danger, border: `1px solid ${colors.danger}40`,
            padding: '10px 20px', borderRadius: 8, fontSize: 14, fontWeight: 600, cursor: 'pointer', fontFamily: fonts.body,
          }}>
            Lock
          </button>
        </div>
      </div>

      {/* Add form */}
      {showAdd && (
        <div style={{
          background: colors.card, borderRadius: 12, padding: 24,
          border: `1px solid ${colors.border}`, marginBottom: 24,
        }}>
          {error && <p style={{ color: colors.danger, fontSize: 14, marginBottom: 12 }}>{error}</p>}
          <input type="text" placeholder="Asset title" value={newItem.title}
            onChange={e => setNewItem({ ...newItem, title: e.target.value })}
            style={{ ...inputStyle, marginBottom: 12 }} />
          <textarea placeholder="Asset content" value={newItem.content}
            onChange={e => setNewItem({ ...newItem, content: e.target.value })}
            rows={4} style={{ ...inputStyle, marginBottom: 12, resize: 'vertical' }} />
          <div style={{ display: 'flex', gap: 12, marginBottom: 12, flexWrap: 'wrap' }}>
            <select value={newItem.category}
              onChange={e => setNewItem({ ...newItem, category: e.target.value })}
              style={{ ...inputStyle, flex: '1 1 200px' }}>
              {categories.filter(c => c.id !== 'all').map(c => (
                <option key={c.id} value={c.id}>{c.label}</option>
              ))}
            </select>
            <input type="text" placeholder="Tags (comma-separated)" value={newItem.tags}
              onChange={e => setNewItem({ ...newItem, tags: e.target.value })}
              style={{ ...inputStyle, flex: '2 1 300px' }} />
          </div>
          <button onClick={addItem} style={{
            background: colors.gold, color: colors.bg, border: 'none', padding: '10px 24px',
            borderRadius: 8, fontSize: 14, fontWeight: 600, cursor: 'pointer', fontFamily: fonts.body,
          }}>
            Encrypt & Save
          </button>
        </div>
      )}

      {/* Search & Categories */}
      <div style={{ display: 'flex', gap: 12, marginBottom: 24, flexWrap: 'wrap' }}>
        <input type="text" placeholder="Search by title or tag..." value={searchQuery}
          onChange={e => setSearchQuery(e.target.value)}
          style={{ ...inputStyle, flex: '1 1 300px' }} />
      </div>
      <div style={{ display: 'flex', gap: 8, marginBottom: 24, flexWrap: 'wrap' }}>
        {categories.map(c => (
          <button key={c.id} onClick={() => setActiveCategory(c.id)} style={{
            background: activeCategory === c.id ? colors.accent : colors.card,
            color: activeCategory === c.id ? colors.textLight : colors.textMuted,
            border: `1px solid ${activeCategory === c.id ? colors.accent : colors.border}`,
            padding: '6px 16px', borderRadius: 20, fontSize: 13, cursor: 'pointer', fontFamily: fonts.body,
          }}>
            {c.label}
          </button>
        ))}
      </div>

      {/* Items */}
      {filtered.length === 0 ? (
        <div style={{ textAlign: 'center', padding: 60, color: colors.textMuted }}>
          <p style={{ fontSize: 16 }}>{items.length === 0 ? 'Your vault is empty. Add your first asset.' : 'No matching assets found.'}</p>
        </div>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(340px, 1fr))', gap: 16 }}>
          {filtered.map(item => (
            <div key={item.id} style={{
              background: colors.card, borderRadius: 12, padding: 20,
              border: `1px solid ${colors.border}`,
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', marginBottom: 8 }}>
                <h4 style={{ fontFamily: fonts.heading, fontSize: 16, color: colors.textLight, margin: 0 }}>{item.title}</h4>
                <div style={{ display: 'flex', gap: 4 }}>
                  <button onClick={() => copyItem(item.content)} title="Copy" style={{
                    background: 'transparent', border: 'none', color: colors.textMuted,
                    cursor: 'pointer', fontSize: 16, padding: '2px 6px',
                  }}>📋</button>
                  <button onClick={() => deleteItem(item.id)} title="Delete" style={{
                    background: 'transparent', border: 'none', color: colors.danger,
                    cursor: 'pointer', fontSize: 16, padding: '2px 6px',
                  }}>🗑</button>
                </div>
              </div>
              <span style={{
                display: 'inline-block', background: colors.accent + '25', color: colors.accentLight,
                padding: '2px 10px', borderRadius: 12, fontSize: 11, fontWeight: 600, marginBottom: 8,
                textTransform: 'uppercase', letterSpacing: 0.5,
              }}>{item.category}</span>
              <p style={{ color: colors.textMuted, fontSize: 13, lineHeight: 1.6, margin: '8px 0',
                maxHeight: 80, overflow: 'hidden',
              }}>{item.content}</p>
              {item.tags?.length > 0 && (
                <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap', marginTop: 8 }}>
                  {item.tags.map((t, i) => (
                    <span key={i} style={{
                      background: colors.gold + '15', color: colors.gold,
                      padding: '2px 8px', borderRadius: 8, fontSize: 11,
                    }}>{t}</span>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── Footer ───
function Footer() {
  return (
    <footer style={{
      fontFamily: fonts.body, padding: '40px 24px',
      borderTop: `1px solid ${colors.border}`,
      textAlign: 'center',
    }}>
      <p style={{ color: colors.textMuted, fontSize: 14 }}>
        © {new Date().getFullYear()} Bad Strategy Group LLC. All rights reserved.
      </p>
    </footer>
  );
}

// ─── Main App ───
function App() {
  const [page, setPage] = useState('home');

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [page]);

  const renderPage = () => {
    switch (page) {
      case 'about': return <AboutPage />;
      case 'services': return <ServicesPage setPage={setPage} />;
      case 'vault': return <VaultPage />;
      default: return <HomePage setPage={setPage} />;
    }
  };

  return (
    <div style={{ background: colors.bg, minHeight: '100vh', color: colors.text }}>
      <Nav page={page} setPage={setPage} />
      {renderPage()}
      <Footer />
    </div>
  );
}

export default App;
